import React from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  Container,
  Box,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Link as MuiLink,
  Divider,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import MenuIcon from "@mui/icons-material/Menu";
import PVTDesignForm from "./components/PVTDesignForm_8";
import HomePage from "./HomePage_2";
import { HashRouter, Routes, Route, useNavigate } from "react-router-dom";
//import axios from "axios";

const HEADER_H = 128; //  AppBar height
const STEPPER_H = 64; // approximate, can tune
const GAP = 12; // spacing below header

const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
    background: {
      default: "#f4f7fb",
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
  },
});

function AppContent() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const mainRef = React.useRef(null);
  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const menuItems = [
    {
      text: "Home",
      icon: <HomeIcon />,
      step: "home",
    },
    {
      text: "Auslegung der Wärmepumpe",
      icon: <InfoIcon />,
      step: 1,
      subItems: [
        { text: "Projectinformation", icon: <InfoIcon />, section: "project" },
        { text: "Gebäude und System", icon: <InfoIcon />, section: "building" },
        { text: "Raumheizung", icon: <InfoIcon />, section: "roomHeating" },
        { text: "Trinkwarmwasser", icon: <InfoIcon />, section: "hotWater" },
      ],
    },
    {
      text: "Auslegungsparameter für PVT-Kollektoren",
      icon: <InfoIcon />,
      step: 2,
      subItems: [
        {
          text: "Auswahl der Wärmepumpe",
          icon: <InfoIcon />,
          section: "operationMode",
        },
        {
          text: "Wetterbedingungen",
          icon: <InfoIcon />,
          section: "heatPumpSpecs",
        },
        { text: "PVT-Modul", icon: <InfoIcon />, section: "pvtModule" },
      ],
    },
    {
      text: "Auslegung der Erdwärmesonden",
      icon: <InfoIcon />,
      step: 3,
      subItems: [
        { text: "EWS Parametern", icon: <InfoIcon />, section: "ews-step" },
      ],
    },
    { text: "Ergebnisse", icon: <InfoIcon />, step: 4 },
  ];

  const navigate = useNavigate();

  // Helper to scroll to a section by id
  const scrollToSection = (sectionId) => {
    const container = mainRef.current;
    const el = document.getElementById(sectionId);
    if (!container || !el) return;

    // how much fixed UI is above the content inside the container
    const offset = STEPPER_H + GAP + 8;

    // element position relative to container
    const containerTop = container.getBoundingClientRect().top;
    const elTop = el.getBoundingClientRect().top;

    const targetScrollTop =
      container.scrollTop + (elTop - containerTop) - offset;

    container.scrollTo({ top: targetScrollTop, behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        display: "flex",
        background: theme.palette.background.default,
        width: "100%",
        minHeight: "100vh",
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          background: "#f5f5f5",
          boxShadow: 3,
          height: 128,
          justifyContent: "center",
          // borderBottom: '1px solid #d6dbe3',
          // zIndex: (theme) => theme.zIndex.drawer + 1, // keeps it above drawer
        }}
      >
        <Toolbar sx={{ minHeight: 96, py: 2 }}>
          <IconButton
            // color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={toggleDrawer(true)}
            sx={{
              mr: 2,
              width: 44,
              height: 44,
              backgroundColor: "#ffffff",
              border: "1px solid #cfd8dc",
              boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
              "&:hover": {
                backgroundColor: "#f0f6ff",
              },
            }}
          >
            <MenuIcon sx={{ color: "#1976d2", fontSize: 26 }} />
          </IconButton>
          <MuiLink
            href="https://isfh.de/"
            target="_blank"
            rel="noopener"
            sx={{ display: "flex", alignItems: "center", mr: 3 }}
            underline="none"
          >
            <img
              src="/isfh_logo.png"
              alt="ISFH Logo"
              style={{ height: 100, marginRight: 24 }}
            />
          </MuiLink>
          <Typography
            variant="h4"
            noWrap
            component="div"
            sx={{
              fontWeight: 800,
              letterSpacing: 1,
              color: "#222",
              flexGrow: 1,
              textAlign: "center",
              fontSize: { xs: 22, sm: 32 },
            }}
          >
            Design-Tool für PVT-Wärmepumpensysteme
          </Typography>
          <MuiLink
            href="https://wp-monitoring.ise.fraunhofer.de/integrate/german/index/index.html"
            target="_blank"
            rel="noopener"
            sx={{ display: "flex", alignItems: "center", ml: 3 }}
            underline="none"
          >
            <img
              src="/integrate_logo.png"
              alt="Integrate Logo"
              style={{ height: 128, marginLeft: 24 }}
            />
          </MuiLink>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            background: "#e3eaf4",
            width: 320,
            pt: 2,
            borderRight: "1px solid #d6dbe3",
            boxShadow: "12px 0 30px rgba(0,0,0,0.18)",
          },
        }}
      >
        <Box
          sx={{ width: 280 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            {menuItems.map((item, idx) => (
              <React.Fragment key={item.text}>
                <ListItem
                  button
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    color: "black",
                    "&:hover": { background: "#9ac5f6ff" },
                  }}
                  onClick={() => {
                    if (item.step === "home") {
                      navigate("/");
                      return;
                    }

                    if (typeof item.step === "number") {
                      const mode = item.step === 3 ? "PVT_BHE" : "PVT_ONLY";

                      navigate("/wpdesign", { state: { mode } });
                      setActiveStep(item.step);

                      const stepToSection = {
                        1: "wp-step",
                        2: "pvt-step",
                        3: "ews-step",
                        4: "results-step",
                      };

                      const target = stepToSection[item.step];
                      if (target) setTimeout(() => scrollToSection(target), 150);
                    }
                  }}

                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
                {item.subItems && (
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem
                        key={subItem.text}
                        button
                        sx={{
                          pl: 4,
                          borderRadius: 2,
                          mb: 1,
                          "&:hover": { background: "#d0e2f7" },
                        }}
                        onClick={() => {
                          if (typeof item.step === "number") {
                            navigate("/wpdesign");
                            setActiveStep(item.step);
                            setTimeout(
                              () => scrollToSection(subItem.section),
                              100,
                            );
                          }
                        }}
                      >
                        <ListItemIcon>{subItem.icon}</ListItemIcon>
                        <ListItemText primary={subItem.text} />
                      </ListItem>
                    ))}
                  </List>
                )}
                {idx < menuItems.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        ref={mainRef}
        sx={{
          width: "100%",
          flexGrow: 1,
          minHeight: "100vh",
          overflow: "auto",
          pt: `${HEADER_H + GAP}px`,
          px: { xs: 1, sm: 3 },
          background: theme.palette.background.default,
        }}
      >
        <Routes>
          <Route
            path="/"
            element={<HomePage setActiveStep={setActiveStep} />}
          />
          <Route
            path="/wpdesign"
            element={
              <Box sx={{ width: "100%", px: { xs: 1, sm: 3 }, pb: 6 }}>
                <PVTDesignForm
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  scrollToSection={scrollToSection}
                />
              </Box>
            }
          />
        </Routes>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <HashRouter>
        <AppContent />
      </HashRouter>
    </ThemeProvider>
  );
}

export default App;

/*
axios.interceptors.request.use(config => {
  console.log("📡 Axios request URL:", config.url);
  console.log("📦 Axios request headers:", config.headers);
  console.log("📑 Axios request data:", config.data);
  return config;
}, error => {
  return Promise.reject(error);
});
*/
